import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import postService from "services/postService";

const usePostsWithPagination = ({
  searchTerm = '',
  location = '',
  showSnackbar = () => {},
  t = () => {},
  mode = '',
  type = ''
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const accessToken = localStorage.getItem('accessToken');
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const fetchPosts = useCallback(async (page = pagination.currentPage) => {
    setLoading(true);
    setError(null);
    try {
      // Check if we're in search mode
      const isSearchMode = searchTerm.trim() !== '' || location.trim() !== '' || type.trim() !== '' || mode.trim() !== '';
      
      let url;
      if (isSearchMode) {
        // Build search URL with all parameters
        url = `${apiUrl}posts/search_recommendation/?`;
        if (searchTerm.trim()) {
          url += `title=${encodeURIComponent(searchTerm.trim())}`;
        }
        if (location.trim()) {
          url += `${url.includes("?") ? "&" : "?"}localisation=${encodeURIComponent(location.trim())}`;
        }
        if (mode.trim()) {
          url += `${url.includes("?") ? "&" : "?"}mode=${encodeURIComponent(mode.trim())}`;
        }
        if (type.trim()) {
          url += `${url.includes("?") ? "&" : "?"}offerType=${encodeURIComponent(type.trim())}`;
        }
        url += `&page=${page}`;
      } else {
        // Regular list URL
        url = `${apiUrl}posts/list/?page=${page}`;
      }

      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('data555',data.results);
      // Handle different response formats
      const processedPosts = isSearchMode ? data.results.map(item => item.post) : data.results;
      setPosts(processedPosts || []);
      
      // Calculate total pages based on count
      const totalPages = Math.ceil(data.count / 10); // Assuming 10 items per page
      
      setPagination({
        currentPage: page,
        totalPages: totalPages,
        totalItems: data.count,
        hasNextPage: !!data.next,
        hasPreviousPage: !!data.previous
      });
    } catch (err) {
      setError(err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, apiUrl, searchTerm, location, type, mode]);

  useEffect(() => {
    fetchPosts(pagination.currentPage);
  }, [fetchPosts, pagination.currentPage]);

  const handleSearch = async (event, page = 1) => {
    console.log('🔍 Starting search with params:', { searchTerm, location, type, mode, page });
    
    if (window.location.pathname.includes('/landing')) {
      navigate('/jobs');
    }

    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (
      searchTerm.trim() === '' &&
      location.trim() === '' &&
      type.trim() === '' &&
      mode.trim() === ''
    ) {
      console.log('⚠️ No search parameters provided');
      showSnackbar(t("searchBox.errors.enter_job_or_city"), "error");
      fetchPosts();
      return;
    }

    setLoading(true);
    try {
      let url = `${apiUrl}posts/search_recommendation/?`;
      if (searchTerm.trim()) {
        url += `title=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (location.trim()) {
        url += `${url.includes("?") ? "&" : "?"}localisation=${encodeURIComponent(location.trim())}`;
      }
      if (mode.trim()) {
        url += `${url.includes("?") ? "&" : "?"}mode=${encodeURIComponent(mode.trim())}`;
      }
      if (type.trim()) {
        url += `${url.includes("?") ? "&" : "?"}offerType=${encodeURIComponent(type.trim())}`;
      }
      url += `&page=${page}`;

      console.log('🌐 Making API request to:', url);

      const response = await fetch(url);
      console.log('📥 Received response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 Received data:', data);

      // Process the posts data
      const processedPosts = data.results.map(item => item.post);
      console.log('🔄 Processed posts:', processedPosts);

      setPosts(processedPosts || []);
      setPagination({
        currentPage: page,
        totalPages: Math.ceil(data.count / 10),
        totalItems: data.count,
        hasNextPage: !!data.next,
        hasPreviousPage: !!data.previous
      });
      
      console.log('✅ Search completed successfully');
    } catch (err) {
      console.error('❌ Search error:', err);
      setError(err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
    }
  }, [pagination.hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
    }
  }, [pagination.hasPreviousPage]);

  const goToPage = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    }
  }, [pagination.totalPages]);

  const refreshPosts = useCallback(() => {
    fetchPosts(pagination.currentPage);
  }, [fetchPosts, pagination.currentPage]);

  return {
    posts,
    loading,
    error,
    pagination,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    refreshPosts,
    handleSearch
  };
};

export default usePostsWithPagination;
